import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useGovernance } from '../../context/GovernanceContext';
import { QUESTIONS, getDimension } from '../../data/questions';
import { MATURITY_LEVELS } from '../../utils/scoring';
import { getActionSteps } from '../../utils/actionHelpers';
import { getFrameworks } from '../../data/australianFrameworks';
import MaturityLabel from '../shared/MaturityLabel';
import Modal from '../shared/Modal';
import ConfirmDialog from '../shared/ConfirmDialog';

// ── Framework badge strip ────────────────────────────────────────────────────
function FrameworkBadges({ keys = [] }) {
  const frameworks = getFrameworks(keys);
  if (frameworks.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {frameworks.map((f) => (
        <span
          key={f.code}
          title={f.fullName}
          className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full cursor-default"
          style={{ backgroundColor: f.bg, color: f.text, border: `1px solid ${f.text}30` }}
        >
          {f.code}
        </span>
      ))}
    </div>
  );
}

// ── Score button strip ──────────────────────────────────────────────────────
function ScoreButtons({ currentScore, onScore }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {MATURITY_LEVELS.map((m) => (
        <button
          key={m.score}
          onClick={() => onScore(m.score)}
          title={m.label}
          className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${
            currentScore === m.score
              ? 'text-white border-transparent'
              : 'bg-white border-gray-200 text-gray-500 hover:border-gray-400'
          }`}
          style={currentScore === m.score ? { backgroundColor: m.color, borderColor: m.color } : {}}
        >
          <span>{m.score}</span>
          <span className="hidden sm:inline">{m.label}</span>
        </button>
      ))}
    </div>
  );
}

// ── Status pill ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  todo:        { label: 'To Do',       bg: 'bg-gray-100',   text: 'text-gray-600'  },
  'in-progress': { label: 'In Progress', bg: 'bg-amber-100',  text: 'text-amber-700' },
  done:        { label: 'Done',        bg: 'bg-green-100',  text: 'text-green-700' },
};

function StatusPill({ status, onClick }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.todo;
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer hover:opacity-80 transition-opacity ${cfg.bg} ${cfg.text}`}
    >
      {cfg.label}
    </button>
  );
}

const STATUS_CYCLE = { todo: 'in-progress', 'in-progress': 'done', done: 'todo' };

// ── Custom action form ───────────────────────────────────────────────────────
function ActionForm({ isOpen, onClose, onSave, clientId, dimNum, initialData }) {
  const questions = QUESTIONS[dimNum] || [];
  const [form, setForm] = useState({
    question_id: questions[0]?.id || '',
    title: '',
    description: '',
    ...initialData,
  });
  const [saving, setSaving] = useState(false);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={initialData ? 'Edit action' : 'Add custom action'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-brand-navy mb-1">Question</label>
          <select
            value={form.question_id}
            onChange={set('question_id')}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-navy"
          >
            {questions.map((q) => (
              <option key={q.id} value={q.id}>
                {q.id.toUpperCase()} — {q.text.split(':')[0]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-brand-navy mb-1">
            Action title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            value={form.title}
            onChange={set('title')}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-navy"
            placeholder="e.g. Commission an external audit"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-brand-navy mb-1">Description</label>
          <textarea
            rows={3}
            value={form.description}
            onChange={set('description')}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-navy resize-none"
            placeholder="Optional detail or steps…"
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded bg-brand-navy text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : initialData ? 'Save changes' : 'Add action'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function DimensionDetail() {
  const { id: clientId, num } = useParams();
  const dimNum = parseInt(num, 10);
  const dim = getDimension(dimNum);
  const questions = QUESTIONS[dimNum] || [];

  const {
    clients,
    loading,
    upsertAssessment,
    updateAction,
    deleteAction,
    addAction,
    getClientAssessments,
    getClientActions,
  } = useGovernance();

  const client = clients.find((c) => c.id === clientId);
  const allAssessments = getClientAssessments(clientId);
  const allActions = getClientActions(clientId).filter((a) => a.dimension === dimNum);

  // Notes state (local only until blur)
  const [notes, setNotes] = useState({});
  const [actionForm, setActionForm] = useState({ open: false, data: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleStepToggle = (action, stepKey) => {
    const steps = getActionSteps(action);
    const current = action.completed_steps || [];
    const next = current.includes(stepKey)
      ? current.filter((k) => k !== stepKey)
      : [...current, stepKey];

    let newStatus = action.status;
    if (next.length === steps.length && steps.length > 0) newStatus = 'done';
    else if (next.length > 0 && action.status === 'todo') newStatus = 'in-progress';
    else if (next.length < steps.length && action.status === 'done') newStatus = 'in-progress';

    updateAction(action.id, { completed_steps: next, status: newStatus });
  };

  const getAssessment = (qid) =>
    allAssessments.find((a) => a.dimension === dimNum && a.question_id === qid);

  const handleScore = (questionId, score) => {
    const current = getAssessment(questionId);
    upsertAssessment(clientId, dimNum, questionId, score, current?.notes ?? '');
  };

  const handleNotesBlur = (questionId) => {
    const noteVal = notes[questionId];
    if (noteVal === undefined) return;
    const current = getAssessment(questionId);
    if (current?.score != null) {
      upsertAssessment(clientId, dimNum, questionId, current.score, noteVal);
    }
  };

  const getNotes = (questionId) => {
    if (notes[questionId] !== undefined) return notes[questionId];
    return getAssessment(questionId)?.notes ?? '';
  };

  const cycleStatus = (action) => {
    updateAction(action.id, { status: STATUS_CYCLE[action.status] || 'todo' });
  };

  const openEditAction = (action) => {
    setActionForm({
      open: true,
      data: {
        id: action.id,
        question_id: action.question_id,
        title: action.title,
        description: action.description || '',
      },
    });
  };

  const handleSaveAction = async (form) => {
    if (actionForm.data?.id) {
      await updateAction(actionForm.data.id, {
        question_id: form.question_id,
        title: form.title,
        description: form.description,
      });
    } else {
      await addAction({ ...form, client_id: clientId, dimension: dimNum });
    }
  };

  const handleDeleteAction = async () => {
    if (!deleteTarget) return;
    await deleteAction(deleteTarget.id);
    setDeleteTarget(null);
  };

  if (loading) return <div className="text-mid-grey text-sm">Loading…</div>;
  if (!client || !dim) return (
    <div className="text-center py-16">
      <p className="text-mid-grey">Not found.</p>
      <Link to="/clients" className="text-brand-teal text-sm hover:underline mt-2 block">
        ← Back to clients
      </Link>
    </div>
  );

  const doneCount = allActions.filter((a) => a.status === 'done').length;

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-mid-grey mb-5 flex-wrap">
        <Link to="/clients" className="hover:text-brand-navy">Clients</Link>
        <span>/</span>
        <Link to={`/clients/${clientId}`} className="hover:text-brand-navy">{client.name}</Link>
        <span>/</span>
        <span className="text-brand-navy font-medium">D{dimNum} · {dim.label}</span>
      </nav>

      {/* Dimension header */}
      <div
        className="rounded-xl p-5 mb-6 text-white"
        style={{ backgroundColor: dim.color }}
      >
        <div className="text-xs font-semibold uppercase tracking-widest opacity-75 mb-1">
          Dimension {dimNum}
        </div>
        <h1 className="text-xl font-bold">{dim.label}</h1>
        <p className="text-sm opacity-75 mt-1">
          APS AI Ethics Principles · DISR Responsible AI Framework · ISO/IEC 42001 · Australian Privacy Act 1988
        </p>
      </div>

      {/* ── Section A: Questions ───────────────────────────────────────── */}
      <section className="mb-8">
        <h2 className="text-lg font-bold text-brand-navy mb-4">Assessment questions</h2>
        <div className="space-y-4">
          {questions.map((q, idx) => {
            const assessment = getAssessment(q.id);
            return (
              <div key={q.id} className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-start gap-3 mb-3">
                  <span
                    className="inline-block rounded-full text-xs font-bold px-2 py-0.5 shrink-0 mt-0.5"
                    style={{ backgroundColor: `${dim.color}20`, color: dim.color }}
                  >
                    {q.id.toUpperCase()}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-brand-navy">{q.text}</p>
                    {assessment?.score && (
                      <MaturityLabel score={assessment.score} size="sm" />
                    )}
                    <FrameworkBadges keys={q.frameworks || []} />
                  </div>
                </div>

                <ScoreButtons
                  currentScore={assessment?.score ?? null}
                  onScore={(score) => handleScore(q.id, score)}
                />

                <div className="mt-3">
                  <textarea
                    rows={2}
                    value={getNotes(q.id)}
                    onChange={(e) =>
                      setNotes((n) => ({ ...n, [q.id]: e.target.value }))
                    }
                    onBlur={() => handleNotesBlur(q.id)}
                    placeholder="Add notes (saved on blur)…"
                    className="w-full border border-gray-200 rounded px-3 py-2 text-xs text-gray-600 focus:outline-none focus:border-brand-navy resize-none"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Section B: Action tracker ──────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-brand-navy">Action tracker</h2>
            {allActions.length > 0 && (
              <p className="text-sm text-mid-grey mt-0.5">
                {doneCount} of {allActions.length} done
              </p>
            )}
          </div>
          <button
            onClick={() => setActionForm({ open: true, data: null })}
            className="text-sm font-semibold text-brand-teal hover:underline"
          >
            + Add custom action
          </button>
        </div>

        {allActions.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <p className="text-mid-grey text-sm">
              No actions yet. Score a question below 3 to auto-generate recommended actions,
              or add a custom action.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {questions.map((q) => {
              const qActions = allActions.filter((a) => a.question_id === q.id);
              if (qActions.length === 0) return null;

              return (
                <div key={q.id}>
                  <div className="text-xs font-semibold text-mid-grey uppercase tracking-wide mb-2 ml-1">
                    {q.id.toUpperCase()} — {q.text.split(':')[0]}
                  </div>
                  <div className="space-y-2">
                    {qActions.map((action) => {
                      const steps = getActionSteps(action);
                      const completedSteps = action.completed_steps || [];
                      const isExpanded = expanded.has(action.id);
                      return (
                        <div
                          key={action.id}
                          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                        >
                          <div className="p-4 flex items-start gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-brand-navy">
                                  {action.title}
                                </span>
                                {action.is_custom && (
                                  <span className="text-xs bg-purple-50 text-purple-600 font-medium px-1.5 py-0.5 rounded">
                                    Custom
                                  </span>
                                )}
                                {steps.length > 0 && (
                                  <span className="text-xs bg-gray-100 text-gray-500 font-medium px-1.5 py-0.5 rounded">
                                    {completedSteps.length}/{steps.length} steps
                                  </span>
                                )}
                              </div>
                              {action.description && (
                                <p className="text-xs text-mid-grey mt-1 leading-relaxed">
                                  {action.description}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <StatusPill
                                status={action.status}
                                onClick={() => cycleStatus(action)}
                              />
                              {(steps.length > 0 || action.is_custom) && (
                                <button
                                  onClick={() => toggleExpanded(action.id)}
                                  className="text-xs text-mid-grey hover:text-brand-navy p-1"
                                  title={isExpanded ? 'Collapse' : 'Expand run sheet'}
                                >
                                  {isExpanded ? '▲' : '▼'}
                                </button>
                              )}
                              <button
                                onClick={() => openEditAction(action)}
                                className="text-xs text-mid-grey hover:text-brand-navy"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => setDeleteTarget(action)}
                                className="text-xs text-red-400 hover:text-red-600"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                              {action.is_custom ? (
                                <p className="text-xs text-mid-grey italic">No run sheet for custom actions.</p>
                              ) : steps.length === 0 ? (
                                <p className="text-xs text-mid-grey italic">No steps defined.</p>
                              ) : (
                                <ol className="space-y-2">
                                  {steps.map((step) => {
                                    const checked = completedSteps.includes(step.key);
                                    return (
                                      <li key={step.key} className="flex items-start gap-2.5">
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() => handleStepToggle(action, step.key)}
                                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-teal accent-brand-teal shrink-0 cursor-pointer"
                                        />
                                        <div>
                                          <p className={`text-xs font-semibold ${checked ? 'line-through text-gray-400' : 'text-brand-navy'}`}>
                                            {step.title}
                                          </p>
                                          <p className="text-xs text-mid-grey mt-0.5 leading-relaxed">{step.body}</p>
                                        </div>
                                      </li>
                                    );
                                  })}
                                </ol>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Custom actions not tied to a specific question shown at bottom */}
            {allActions
              .filter((a) => !questions.find((q) => q.id === a.question_id))
              .map((action) => {
                const isExpanded = expanded.has(action.id);
                return (
                  <div
                    key={action.id}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                  >
                    <div className="p-4 flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-brand-navy">{action.title}</span>
                          <span className="text-xs bg-purple-50 text-purple-600 font-medium px-1.5 py-0.5 rounded">
                            Custom
                          </span>
                        </div>
                        {action.description && (
                          <p className="text-xs text-mid-grey mt-1">{action.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <StatusPill status={action.status} onClick={() => cycleStatus(action)} />
                        <button
                          onClick={() => toggleExpanded(action.id)}
                          className="text-xs text-mid-grey hover:text-brand-navy p-1"
                          title={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? '▲' : '▼'}
                        </button>
                        <button
                          onClick={() => openEditAction(action)}
                          className="text-xs text-mid-grey hover:text-brand-navy"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteTarget(action)}
                          className="text-xs text-red-400 hover:text-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                        <p className="text-xs text-mid-grey italic">No run sheet for custom actions.</p>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </section>

      {/* Modals */}
      <ActionForm
        isOpen={actionForm.open}
        onClose={() => setActionForm({ open: false, data: null })}
        onSave={handleSaveAction}
        clientId={clientId}
        dimNum={dimNum}
        initialData={actionForm.data}
      />

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteAction}
        title="Delete action"
        message={`Delete "${deleteTarget?.title}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}
